<template>
    <div class="tabsview">
        <v-tabs
            color="primary"
            :optional="allowEmpty"
            :value="tabindex"
            :key="tabs.map(tab => tab.name).join(',')"
            show-arrows
            @change="onTabChange"
            @touchstart.native="onTabBarTouchStart"
            @touchmove.native="onTabBarTouchMove"
        >
            <v-tab
                class="px-0 mr-4"
                v-for="(tab, index) in tabs"
                :key="`${index}`"
                :disabled="tab.disabled"
            >
                {{ tab.label }}

                <v-chip
                    small
                    v-if="tab.counter || tab.showCounter"
                    :color="color(tab)"
                    class="ml-2 justify-center"
                    :class="textColorClass(tab)"
                    :outlined="outlined(tab)"
                >
                    {{ tab.counter }}</v-chip
                >
            </v-tab>
        </v-tabs>
        <v-tabs-items
            v-if="!allowEmpty || tabindex >= 0"
            :value="tabindex"
            touchless
        >
            <slot name="tabs"> </slot>
        </v-tabs-items>
    </div>
</template>

<script>
export default {
    model: { prop: "value", event: "change" },
    props: {
        allowEmpty: Boolean,
        tabs: {
            type: Array,
            required: true,
        },
        value: {
            type: String,
            required: false,
        },
    },
    data() {
        return {
            tabBarTouchX: null,
            tabBarSwiped: false,
        };
    },
    computed: {
        tabs_list() {
            return this.tabs.map((item) => item.name);
        },
        tabindex() {
            return this.tabs_list.indexOf(this.value);
        },
    },
    methods: {
        onTabBarTouchStart(event) {
            const touch = event.changedTouches && event.changedTouches[0];
            this.tabBarTouchX = touch ? touch.clientX : null;
            this.tabBarSwiped = false;
        },
        onTabBarTouchMove(event) {
            if (this.tabBarTouchX == null) {
                return;
            }
            const touch = event.changedTouches && event.changedTouches[0];
            if (!touch) {
                return;
            }
            if (Math.abs(touch.clientX - this.tabBarTouchX) > 12) {
                this.tabBarSwiped = true;
            }
        },
        onTabChange(index) {
            if (this.allowEmpty && index == null) {
                this.$emit("change", null);
                return;
            }
            if (this.tabBarSwiped) {
                this.tabBarSwiped = false;
                return;
            }
            if (index < 0 || index >= this.tabs_list.length) {
                return;
            }
            this.$emit("change", this.tabs_list[index]);
        },
        active(tab) {
            return tab.name == this.value;
        },
        textColorClass(tab) {
            if (!tab.counter_dim) {
                return "white--text";
            }
            return "grey--text";
        },
        color(tab) {
            if (tab.counter_color) {
                return tab.counter_color;
            }
            if (tab.counter_dim && !this.active(tab)) {
                return "grey";
            }
            return "primary";
        },
        outlined(tab) {
            if (tab.counter_dim) {
                return true;
            }
            return false;
        },
    },
};
</script>

<style lang="scss">
.tabsview {
    .v-tabs-items {
        overscroll-behavior-x: contain;
    }

    // Vuetify 2 applies the slide on v-tab-item (VWindowItem), not v-tabs-items.
    // Kill enter/leave transforms so tab content swaps instantly.
    .v-window-x-transition-enter-active,
    .v-window-x-transition-leave-active,
    .v-window-x-reverse-transition-enter-active,
    .v-window-x-reverse-transition-leave-active,
    .v-window-y-transition-enter-active,
    .v-window-y-transition-leave-active,
    .v-window-y-reverse-transition-enter-active,
    .v-window-y-reverse-transition-leave-active {
        transition: none !important;
    }

    .v-window-x-transition-enter,
    .v-window-x-transition-leave-to,
    .v-window-x-reverse-transition-enter,
    .v-window-x-reverse-transition-leave-to,
    .v-window-y-transition-enter,
    .v-window-y-transition-leave-to,
    .v-window-y-reverse-transition-enter,
    .v-window-y-reverse-transition-leave-to {
        opacity: 1 !important;
        transform: none !important;
    }

    // Keep the first tab aligned: overlay the affix arrows just inside the
    // card edge instead of letting them take 52px in the flex flow.
    .v-tabs {
        overflow: visible;
    }

    .v-slide-group--has-affixes {
        position: relative;
    }

    .v-slide-group__prev,
    .v-slide-group__next {
        position: absolute;
        top: 0;
        bottom: 0;
        z-index: 2;
        min-width: 36px;
        width: 36px;
        flex: 0 0 36px;
    }

    .v-slide-group__prev {
        left: -32px;
    }

    .v-slide-group__next {
        right: -32px;
    }

    .v-slide-group__prev--disabled,
    .v-slide-group__next--disabled {
        visibility: hidden;
        pointer-events: none;
    }
}
</style>
